import React, { FunctionComponent, useState } from "react"
import { Controller, useFieldArray, useFormContext } from "react-hook-form"
import { Input } from "../../../components/Form"
import FormInput from "../../../components/Form/Inputs/FormInput"
import SelectInput from "../../../components/Form/Select/Select"
import { collectionsList, spaceSizes } from "../../../api/Definitions"
import { iPackageRule } from "../../../definitions/Package"
import AsyncSelectInput from "../../../components/Form/Select/AsyncSelect"
import { get, handleDeleteData } from "../../../store/data/actions"
import { iRoles } from "../../../definitions/Roles"
import { iResponse } from "../../../definitions/interfaces"
import s from "../Packages.less"
import { useHistory } from "react-router"

const Overview: FunctionComponent = () => {
  const history = useHistory()
  const { control, getValues } = useFormContext()
  const { fields, remove, append } = useFieldArray({
    name: "rules",
    control,
  })

  const {
    fields: oneTimeFields,
    remove: oneTimeRemove,
    append: oneTimeAppend,
  } = useFieldArray({
    name: "oneTime",
    control,
  })

  const loadRoles = async (input: string) => {
    const result = await get<iResponse<iRoles[]>>({
      link: "/v1/roles-list",
      query: { name: input },
    })
    return result.data
  }

  const handleDelete = () => {
    handleDeleteData({
      link: "/v1/packages/" + getValues("_id"),
    }).then(() => {
      history.goBack()
    })
  }

  return (
    <div className="scrollable container">
      <FormInput name="name" label="Name" />
      <FormInput name="planId" label="Plan ID" />
      <FormInput name="internalName" label="Internal Name" />
      <FormInput
        type="number"
        name="artPiecesPerExhibition"
        label="Art pieces per exhibition"
      />

      <FormInput type="checkbox" name="isCustom" label="Is custom package?" />

      <Controller
        control={control}
        name="userRole"
        render={({ value, onChange }) => (
          <AsyncSelectInput
            value={value}
            onChange={onChange}
            loadOptions={loadRoles}
            name="userRole"
            label="User role"
            defaultOptions
          />
        )}
      />
      <Controller
        control={control}
        name="space"
        render={({ value, onChange }) => (
          <SelectInput
            value={value}
            onChange={onChange}
            options={spaceSizes}
            name="space"
            label="Space size"
            isMulti
          />
        )}
      />
      <h3 className={s.ruleTitle}>Rules</h3>
      {fields.map((field, index) => (
        <div className="row" key={field.id}>
          <div className="col-4">
            <Controller
              render={({ value, onChange }) => (
                <SelectInput
                  name={`rules[${index}].collection`}
                  options={collectionsList}
                  placeholder="Collection..."
                  value={value}
                  onChange={onChange}
                />
              )}
              name={`rules[${index}].collection`}
              defaultValue={field.collection}
            />
          </div>
          <div className="col-4">
            <Controller
              render={({ value, onChange }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  name={`rules[${index}].count`}
                  placeholder="Count"
                  type="number"
                />
              )}
              name={`rules[${index}].count`}
              control={control}
              defaultValue={field.count}
            />
          </div>
          <div className="col-4">
            <div className="form-group">
              <div className="form-control">
                <button
                  className="btn-red"
                  onClick={() => {
                    remove(index)
                  }}
                  type="button"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
      <CreateRule append={append} />
      <h3 className={s.ruleTitle}>One time rules</h3>
      {oneTimeFields.map((field, index) => (
        <div className="row" key={field.id}>
          <div className="col-4">
            <Controller
              render={({ value, onChange }) => (
                <SelectInput
                  name={`oneTime[${index}].collection`}
                  options={collectionsList}
                  placeholder="Collection..."
                  value={value}
                  onChange={onChange}
                />
              )}
              name={`oneTime[${index}].collection`}
              defaultValue={field.collection}
            />
          </div>
          <div className="col-4">
            <Controller
              render={({ value, onChange }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  name={`oneTime[${index}].count`}
                  placeholder="Count"
                  type="number"
                />
              )}
              name={`oneTime[${index}].count`}
              control={control}
              defaultValue={field.count}
            />
          </div>
          <div className="col-4">
            <div className="form-group">
              <div className="form-control">
                <button
                  className="btn-red"
                  onClick={() => {
                    oneTimeRemove(index)
                  }}
                  type="button"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
      <CreateRule append={oneTimeAppend} title="Create one time rule" />
      <div className={s.buttons}>
        <button className="btn-blue submit-button">Submit</button>
        <button
          onClick={handleDelete}
          className={"btn-red submit-button"}
          type="button"
        >
          Delete
        </button>
      </div>
    </div>
  )
}

export default Overview

interface iProps {
  append: (value: iPackageRule) => void
  title?: string
}

export const CreateRule: FunctionComponent<iProps> = ({
  append,
  title = "Create rule",
}) => {
  const [collection, setCollection] = useState<string>("")
  const [count, setCount] = useState<number>(0)
  const addRule = () => {
    append({ collection, count })
    setCount(0)
    setCollection("")
  }

  return (
    <div className="container">
      <h4>{title}</h4>
      <div className="row">
        <div className="col-4">
          <SelectInput
            name="collection"
            options={collectionsList}
            placeholder="Collection..."
            onChange={(val) => {
              setCollection(val)
            }}
            value={collection}
          />
        </div>
        <div className="col-4">
          <Input
            name="count"
            placeholder="Count"
            onChange={(value) => {
              setCount(value as number)
            }}
            type="number"
            value={count}
          />
        </div>
        <div className="col-4">
          <div className="form-group">
            <div className="form-control">
              <button
                className="btn-blue"
                onClick={() => {
                  addRule()
                }}
                type="button"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
